import {GqlModel, GqlModelListOperator, GqlModelOperator, GqlQueryModel} from "../graphql/graphql.model";
import gql from "graphql-tag";
import {jsonListIndex, JSONModel, jsonProperty} from "../json.model";
import {EditableListItem} from "../graphql/common.gql-model";

const accountAmounts: any = gql`
  fragment accountAmounts on AccountAmount {
    localAmount
    usdAmount
  }
  
  fragment accountAmountGroups on AccountItemWithAmount {
    budget {
      ...accountAmounts
    }
    expenses {
      ...accountAmounts
    }
    remaining {
      ...accountAmounts
    }
  }
`;

export class ExpenseGqlModel extends GqlQueryModel implements EditableListItem {
  static mapper: GqlModelListOperator<ExpenseGqlModel> = GqlModel.buildListMapper(ExpenseGqlModel, "expenses");

  static query: any = gql`
    query getExpenses($projectId: ID!, $bucket: String) {
      expenses: expenses(projectId: $projectId, bucket: $bucket) {
        id
        accountId
        month
        period
        localAmount
        usdAmount
        currency
        attribute
        comment
        costLocationCode
        employeeName
        projectName
        source
        trackingType
        sourceDB
        bucket
        createdBy
        updatedBy
        createdAt
        updatedAt
        roleName
      }
    }
  `;

  @jsonProperty("string") id: string;
  @jsonProperty("string") accountId: string;
  @jsonProperty("string") month: string;
  @jsonProperty("string") period: string;
  @jsonProperty("number0") localAmount: number;
  @jsonProperty("number0") usdAmount: number;
  @jsonProperty("string") currency: string;
  @jsonProperty("string") attribute: string;
  @jsonProperty("string") comment: string;
  @jsonProperty("string") costLocationCode: string;
  @jsonProperty("string") employeeName: string;
  @jsonProperty("string") projectName: string;
  @jsonProperty("string") source: string;
  @jsonProperty("string") trackingType: string;
  @jsonProperty("string") sourceDB: string;
  @jsonProperty("string") bucket: string;
  @jsonProperty("string") createdBy: string;
  @jsonProperty("string") updatedBy: string;
  @jsonProperty("date") createdAt: Date;
  @jsonProperty("date") updatedAt: Date;
  @jsonProperty("string") roleName: string;

  editing: boolean = false;
  @jsonListIndex() index: number;

  static variables(projectId: string, bucket?: string) {
    return {
      projectId,
      bucket: bucket || null,
    };
  }
}

export class AccountAmountModel extends JSONModel {
  @jsonProperty("number0") localAmount: number;
  @jsonProperty("number0") usdAmount: number;
}

export type AmountType = keyof AccountAmountModel;

export class AccountBucketMonthModel extends JSONModel {
  @jsonProperty("string") month: string;
  @jsonProperty(AccountAmountModel) expenses: AccountAmountModel;
  @jsonProperty(AccountAmountModel) budget: AccountAmountModel;
  @jsonProperty(AccountAmountModel) remaining: AccountAmountModel;
}

export class AccountBucketModel extends JSONModel {
  @jsonProperty("string") bucket: string;
  @jsonProperty([AccountBucketMonthModel]) months: AccountBucketMonthModel[];
  @jsonProperty(AccountAmountModel) expenses: AccountAmountModel;
  @jsonProperty(AccountAmountModel) budget: AccountAmountModel;
  @jsonProperty(AccountAmountModel) remaining: AccountAmountModel;
}

export class AccountSummaryGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<AccountSummaryGqlModel> =
    GqlModel.buildMapper(AccountSummaryGqlModel, "accountSummary");

  static query: any = gql`
    ${accountAmounts}

    query getAccountSummary($projectId: ID!) {
      accountSummary: accountSummary(projectId: $projectId) {
        buckets {
          bucket
          months {
            month
            ...accountAmountGroups
          }
          ...accountAmountGroups
        }
        ...accountAmountGroups
        projectCurrency
        projectName
      }
    }
  `;

  @jsonProperty([AccountBucketModel]) buckets: AccountBucketModel[];
  @jsonProperty(AccountAmountModel) expenses: AccountAmountModel;
  @jsonProperty(AccountAmountModel) budget: AccountAmountModel;
  @jsonProperty(AccountAmountModel) remaining: AccountAmountModel;

  @jsonProperty("string") projectCurrency: string;
  @jsonProperty("string") projectName: string;

  static variables(projectId: string) {
    return {projectId};
  }
}

export class AccountInfoModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") regionalName: string;
  @jsonProperty("boolean") selected: string;
}

export class AccountListModel extends GqlQueryModel {
  static mapper: GqlModelOperator<AccountListModel> = GqlModel.buildMapper(AccountListModel);

  static query: any = gql`
    query getAccountList($projectId: ID!, $search: String, $page: Int, $limit: Int) {
      rows: accountList(projectId: $projectId, search: $search, page: $page, limit: $limit) {
        id
        regionalName
      }
      
      count: accountCount(projectId: $projectId, search: $search)
    }
  `;

  static variables(projectId: string, search: string, page: number, limit: number) {
    return {projectId, search, page, limit};
  }

  @jsonProperty([AccountInfoModel]) rows: AccountInfoModel[];
  @jsonProperty("number0") count: number;
}
